import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilTrash } from "@coreui/icons";
import { useCartStore, type ExtendLine } from "../../state/cartStore";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import {
  useExtensionPreview,
  type ExtensionPreviewDto,
} from "../../api/hooks/useStrategySubscriptions";
import { offersForLine, usableOffers, useUsableOffers } from "../../api/hooks/useOffers";
import { routePaths } from "../../routes/route-paths";
import { formatPriceUsd } from "../../lib/money";
import { formatDateShort, formatDayMonth } from "../../lib/dates";
import { extensionRenewalUsd } from "../../lib/extension-figures";
import { StrategyIdentityBand } from "./components/StrategyIdentityBand";
import { StrategyMetaPills } from "./components/StrategyMetaPills";
import { StrategySideLabel } from "./components/StrategySideLabel";
import { ExtendLineOffer } from "./components/ExtendLineOffer";
import { ExtendDiscountWarning } from "./components/ExtendDiscountWarning";

/**
 * The cart while it holds an extension. Every figure is Stripe's, asked for when the line goes in and
 * again whenever the offer changes, so what she sees is what she is charged.
 */
export function ExtendCart({ line }: { line: ExtendLine }) {
  const navigate = useNavigate();
  const { data: strategies } = useTradingStrategies();
  const offers = usableOffers(useUsableOffers().data);
  const removeExtension = useCartStore((s) => s.removeExtension);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const preview = useExtensionPreview(line.strategyId, line.offerId);

  const strategy = strategies?.find((s) => s.id === line.strategyId);
  const offer =
    offersForLine(offers, line.strategyId, "Yearly").find((o) => o.id === line.offerId) ?? null;

  const confirmRemove = () => {
    removeExtension();
    setConfirmingRemoval(false);
  };

  return (
    <div>
      <h2>Your cart</h2>
      <p className="text-body-secondary mb-3">
        Check what extending costs and the new dates, then proceed to checkout.
      </p>

      <CCard>
        <CCardHeader>Extend your year</CCardHeader>
        <CCardBody className="p-0">
          {strategy ? <StrategyIdentityBand strategy={strategy} /> : <div className="strategy-band" />}
          <div className="d-flex align-items-center justify-content-between gap-3 p-3">
            <div className="flex-grow-1 min-width-0">
              <Link to={`/subscriptions/${line.strategyId}`} className="text-reset text-decoration-none">
                <h5 className="mb-0">{line.name}</h5>
              </Link>
              {strategy && (
                <StrategySideLabel direction={strategy.direction} className="small fw-semibold mt-1" />
              )}
              {strategy && (
                <div className="d-flex flex-wrap gap-1 mt-2">
                  <StrategyMetaPills strategy={strategy} withRiskProfile withTimeframe={false} />
                </div>
              )}
            </div>
            <div className="d-flex align-items-center gap-3 flex-shrink-0">
              {preview.data && (
                <span className="fs-5 fw-bold">
                  {preview.data.newYearUsd < preview.data.yearPriceUsd && (
                    <span className="text-body-secondary fw-normal fs-6 text-decoration-line-through me-2">
                      {formatPriceUsd(preview.data.yearPriceUsd)}
                    </span>
                  )}
                  {formatPriceUsd(preview.data.newYearUsd)}
                  <span className="text-body-secondary fw-normal small"> /yr</span>
                </span>
              )}
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                onClick={() => setConfirmingRemoval(true)}
                aria-label={`Remove the extension of ${line.name} from cart`}
              >
                <CIcon icon={cilTrash} />
              </CButton>
            </div>
          </div>

          <div className="px-3 pb-3">
            <ExtendLineOffer line={line} offers={offers} />

            {preview.isLoading && (
              <div className="text-center py-4 text-body-secondary">
                <CSpinner size="sm" className="me-2" />
                Asking Stripe what extending comes to…
              </div>
            )}

            {preview.error && (
              <CAlert color="warning" className="mt-3 mb-0">
                {preview.error.message}
              </CAlert>
            )}

            {preview.data && (
              <>
                <ExtendDiscountWarning
                  strategyName={line.name}
                  preview={preview.data}
                  chosenOfferId={line.offerId}
                  offers={offers}
                />
                <p className="mt-3 mb-0">
                  Your year runs to <strong>{formatDateShort(preview.data.currentPeriodEnd)}</strong>.
                  Extending restarts it today, so it runs to{" "}
                  <strong>{formatDateShort(preview.data.newPeriodEnd)}</strong>.
                </p>
                <ExtensionBreakdown name={line.name} preview={preview.data} offerName={offer?.publicName ?? null} />
                <p className="text-body-secondary small mt-2 mb-0">
                  Then {formatPriceUsd(extensionRenewalUsd(preview.data, offer))} every{" "}
                  {formatDayMonth(preview.data.newPeriodEnd)}.
                </p>
              </>
            )}
          </div>
        </CCardBody>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <Link to={routePaths.strategies} className="btn btn-outline-secondary">
          Continue browsing
        </Link>
        <CButton
          color="primary"
          disabled={!preview.data}
          onClick={() => navigate(routePaths.checkout)}
        >
          Checkout
        </CButton>
      </div>

      <CModal visible={confirmingRemoval} onClose={() => setConfirmingRemoval(false)} alignment="center">
        <CModalHeader>
          <CModalTitle>Remove from cart?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            Remove the extension of <strong>{line.name}</strong> from your cart? Its year stays as it
            is, and you can extend any time from its subscription page.
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setConfirmingRemoval(false)}>
            Cancel
          </CButton>
          <CButton color="danger" onClick={confirmRemove}>
            Remove
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

/** The whole order for an extension, as Stripe works it out. Shared by the cart and Review. */
export function ExtensionBreakdown({
  name,
  preview,
  offerName,
}: {
  name: string;
  preview: ExtensionPreviewDto;
  offerName: string | null;
}) {
  const offerSaving = preview.yearPriceUsd - preview.newYearUsd;

  return (
    <div className="border rounded mt-3 px-3">
      <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
        <span>A year of {name}</span>
        <span className="text-nowrap">{formatPriceUsd(preview.yearPriceUsd)}</span>
      </div>
      {offerSaving > 0 && (
        <div className="d-flex justify-content-between gap-3 py-2 border-bottom text-success">
          <span>{offerName ?? "Offer"}</span>
          <span className="text-nowrap">−{formatPriceUsd(offerSaving)}</span>
        </div>
      )}
      <div className="d-flex justify-content-between gap-3 py-2 border-bottom text-success">
        <span>
          Time you haven't used
          <span className="d-block text-body-secondary small">At what you paid for it</span>
        </span>
        <span className="text-nowrap">−{formatPriceUsd(preview.unusedTimeCreditUsd)}</span>
      </div>
      <div className={`d-flex justify-content-between gap-3 py-2 fw-bold${preview.leftOnAccountUsd > 0 ? " border-bottom" : ""}`}>
        <span>Due today</span>
        <span className="text-nowrap">{formatPriceUsd(preview.dueTodayUsd)}</span>
      </div>
      {preview.leftOnAccountUsd > 0 && (
        <div className="d-flex justify-content-between gap-3 py-2">
          <span>
            <strong>Left as credit on your account</strong>
            <span className="d-block text-body-secondary small">
              Comes off your next payments first, for any strategy. It isn't paid out.
            </span>
          </span>
          <span className="text-nowrap text-success fw-semibold">
            {formatPriceUsd(preview.leftOnAccountUsd)}
          </span>
        </div>
      )}
    </div>
  );
}
